import React from 'react'
import {Link, useRouteLoaderData} from '@remix-run/react'

import clsx from 'clsx'
import type {RootLoaderResponse} from '~/types'
import {getTranslation} from '~/localization/translation'

const styles = {
  container: 'px-10 text-sm tablet:px-5',
  content: 'content-container mx-auto my-10 text-[14px] mobile:my-6',
  title:
    'text-[48px] !capitalize font-semibold leading-[58px] tablet:text-[32px] tablet:leading-[40px] mobile:text-3xl tablet:text-center',
  divider: 'mb-10 mt-10 h-1 w-full bg-gray-400 tablet:mb-6 tablet:mt-6',
  section: 'mb-8 tablet:mb-4',
  heading2:
    'text-[24px] font-semibold leading-[32px] text-[#363636] mb-4 tablet:text-[20px] tablet:leading-[28px] tablet:mb-3',
  paragraph: 'text-[16px] leading-[22px] text-[#363636] mb-4 tablet:text-[14px] tablet:leading-[20px] tablet:mb-3',
  list: 'list-disc pl-10 mb-4 tablet:pl-6 tablet:mb-3',
  listItem: 'text-[16px] leading-[22px] text-[#363636] mb-2 tablet:text-[14px] tablet:leading-[20px] tablet:mb-1',
  subList: 'mt-2 list-[circle] pl-10 tablet:pl-6',
  subListItem: 'text-[16px] leading-[22px] text-[#363636] mb-1 tablet:text-[14px] tablet:leading-[20px]',
  table: 'w-full border-collapse mb-0 shadow-sm min-w-[600px]',
  tableHeader: 'bg-gray-100 border-b border-gray-300',
  tableHeaderCell:
    'px-4 py-3 text-base font-semibold text-[#363636] border-r border-gray-300 last:border-r-0 text-center tablet:px-3 tablet:py-2 tablet:text-sm',
  tableRow: 'border-b border-gray-200 hover:bg-gray-50 transition-colors',
  tableCell:
    'px-4 py-3 text-sm text-[#363636] border-r border-gray-300 last:border-r-0 tablet:px-3 tablet:py-2 tablet:text-xs',
  link: 'text-blue-600 underline hover:text-blue-800 transition-colors',
  bold: 'font-semibold',
} as const

const Section: React.FC<{children: React.ReactNode}> = ({children}) => (
  <section className={styles.section}>{children}</section>
)

const Heading2: React.FC<{children: React.ReactNode}> = ({children}) => <h2 className={styles.heading2}>{children}</h2>

const Paragraph: React.FC<{children: React.ReactNode}> = ({children}) => <p className={styles.paragraph}>{children}</p>

const List: React.FC<{children: React.ReactNode}> = ({children}) => <ul className={styles.list}>{children}</ul>

const ListItem: React.FC<{children: React.ReactNode}> = ({children}) => <li className={styles.listItem}>{children}</li>

const SubList: React.FC<{children: React.ReactNode}> = ({children}) => <ul className={styles.subList}>{children}</ul>

const SubListItem: React.FC<{children: React.ReactNode}> = ({children}) => (
  <li className={styles.subListItem}>{children}</li>
)

const getItemText = (item: any): string => {
  return typeof item === 'string' ? item : item.itemText
}

const renderTextWithPrivacyPolicyLink = (text: string) => {
  const privacyPolicyRegex = /Privacy Policy/gi
  const htmlText = text.replace(
    privacyPolicyRegex,
    '<a href="/privacy-policy" class="text-blue-600 underline hover:text-blue-800 transition-colors" aria-label="go to privacy policy page">Privacy Policy</a>',
  )

  return <span dangerouslySetInnerHTML={{__html: htmlText}} />
}

const renderListItem = (item: any) => {
  const itemText = getItemText(item)

  return (
    <ListItem key={itemText}>
      {renderTextWithPrivacyPolicyLink(itemText)}
      {typeof item === 'object' && item.subItems && item.subItems.length > 0 && (
        <SubList>
          {item.subItems.map((subItem: any) => {
            const subItemText = getItemText(subItem)
            return <SubListItem key={subItemText}>{renderTextWithPrivacyPolicyLink(subItemText)}</SubListItem>
          })}
        </SubList>
      )}
    </ListItem>
  )
}

const renderSimpleListItem = (item: any) => {
  const itemText = getItemText(item)
  return <ListItem key={itemText}>{renderTextWithPrivacyPolicyLink(itemText)}</ListItem>
}

const Ccpa = () => {
  const {language, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>{translations.CCPA.title}</h1>
        <hr className={styles.divider} />

        <Section>
          <div className="text-base leading-relaxed text-gray-800 tablet:text-sm tablet:leading-relaxed">
            {translations.CCPA.privacyNotice.map((chunk, chunkIndex) => {
              const value = typeof chunk.value === 'function' ? chunk.value(domain) : chunk.value
              const valueStr = String(value).substring(0, 50)
              const key =
                chunk.type === 'link'
                  ? `chunk-${chunkIndex}-${chunk.type}-${chunk.href}-${valueStr}`
                  : `chunk-${chunkIndex}-${chunk.type}-${valueStr}`

              if (chunk.type === 'bold') {
                return (
                  <strong key={key} className={styles.bold}>
                    {value}
                  </strong>
                )
              }
              if (chunk.type === 'link') {
                return (
                  <Link key={key} to={chunk.href ?? ''} className={styles.link} aria-label={`go to ${chunk.href} page`}>
                    {value}
                  </Link>
                )
              }
              return <span key={key}>{value}</span>
            })}
          </div>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p1.header}</Heading2>
          <Paragraph>{translations.CCPA.p1.part_1}</Paragraph>

          <div className="lg:overflow-x-visible mb-6 overflow-x-auto rounded-lg border border-gray-300 tablet:mb-4">
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  {translations.CCPA.p1.part_1_table.columns.map((column) => (
                    <th key={column.id} className={styles.tableHeaderCell}>
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {translations.CCPA.p1.part_1_table.rows.map((row) => {
                  const rowKey = translations.CCPA.p1.part_1_table.columns
                    .map((column) => `${column.id}-${row[column.id as keyof typeof row]}`)
                    .join('|')
                  return (
                    <tr key={rowKey} className={styles.tableRow}>
                      {translations.CCPA.p1.part_1_table.columns.map((column) => (
                        <td key={`${rowKey}-${column.id}`} className={styles.tableCell}>
                          {row[column.id as keyof typeof row]}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <Paragraph>{translations.CCPA.p1.part_2}</Paragraph>
          <List>
            {translations.CCPA.p1.part_2_listItems.map((item) => (
              <ListItem key={item.itemText}>{renderTextWithPrivacyPolicyLink(item.itemText)}</ListItem>
            ))}
          </List>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p2.header}</Heading2>
          <Paragraph>{translations.CCPA.p2.part_1}</Paragraph>
          <List>{translations.CCPA.p2.part_1_listItems.map(renderSimpleListItem)}</List>
          <Paragraph>{translations.CCPA.p2.part_2}</Paragraph>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p3.header}</Heading2>
          <Paragraph>{translations.CCPA.p3.part_1}</Paragraph>
          <Paragraph>{translations.CCPA.p3.part_2}</Paragraph>
          <List>{translations.CCPA.p3.part_2_listItems.map(renderListItem)}</List>
          <Paragraph>{translations.CCPA.p3.part_3}</Paragraph>
          <Paragraph>{translations.CCPA.p3.part_4}</Paragraph>
          <Paragraph>{translations.CCPA.p3.part_5}</Paragraph>
          <List>{translations.CCPA.p3.part_5_listItems.map(renderSimpleListItem)}</List>
          <Paragraph>{translations.CCPA.p3.part_6}</Paragraph>
          <Paragraph>{translations.CCPA.p3.part_7}</Paragraph>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p4.header}</Heading2>
          <Paragraph>{translations.CCPA.p4.part_1}</Paragraph>
          <p className={clsx('italic', styles.paragraph)}>{translations.CCPA.p4.part_2}</p>
          <Paragraph>{translations.CCPA.p4.part_3}</Paragraph>
          <List>{translations.CCPA.p4.part_3_listItems.map(renderSimpleListItem)}</List>

          <p className={clsx('italic', styles.paragraph)}>{translations.CCPA.p4.part_4}</p>
          <Paragraph>{translations.CCPA.p4.part_5}</Paragraph>
          <Paragraph>{translations.CCPA.p4.part_6}</Paragraph>
          <List>{translations.CCPA.p4.part_6_listItems.map(renderSimpleListItem)}</List>

          <p className={clsx('italic', styles.paragraph)}>{translations.CCPA.p4.part_7}</p>
          <Paragraph>{translations.CCPA.p4.part_8}</Paragraph>
          <Paragraph>{translations.CCPA.p4.part_9}</Paragraph>
          <Paragraph>{translations.CCPA.p4.part_10}</Paragraph>
          <List>{translations.CCPA.p4.part_10_listItems.map(renderSimpleListItem)}</List>
          <Paragraph>{translations.CCPA.p4.part_11}</Paragraph>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p5.header}</Heading2>
          <Paragraph>{translations.CCPA.p5.part_1}</Paragraph>
          <Paragraph>{translations.CCPA.p5.part_2}</Paragraph>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p6.header}</Heading2>
          <Paragraph>
            {translations.CCPA.p6.part_1}{' '}
            <Link aria-label="go to sharing policy page" className={styles.link} prefetch="intent" to="/sharing-policy">
              {`${domain}/sharing-policy`}
            </Link>
          </Paragraph>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p7.header}</Heading2>
          <Paragraph>{translations.CCPA.p7.part_1}</Paragraph>
          <List>{translations.CCPA.p7.part_1_listItems.map(renderSimpleListItem)}</List>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p8.header}</Heading2>
          <Paragraph>{translations.CCPA.p8.part_1}</Paragraph>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p9.header}</Heading2>
          <Paragraph>{translations.CCPA.p9.part_1}</Paragraph>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p10.header}</Heading2>
          <Paragraph>{translations.CCPA.p10.part_1}</Paragraph>
          <p className={clsx('font-semibold', styles.paragraph)}>{translations.CCPA.p10.part_2}</p>
        </Section>

        <Section>
          <Heading2>{translations.CCPA.p11.header}</Heading2>
          <Paragraph>{translations.CCPA.p11.part_1}</Paragraph>
        </Section>

        <p className="mt-20 italic tablet:mt-10">Updated {translations.CCPA.updatedDate}</p>
      </div>
    </div>
  )
}

export default Ccpa
